import React, { useState, useEffect } from 'react';
import { fetchProductList, fetchProductListPurchase } from '../services/api';

const todayIso = new Date().toISOString().split('T')[0];

function toApiDt(iso) {
  // 'YYYY-MM-DD' → '2026-03-18T00:00:00'
  return iso ? `${iso}T00:00:00` : '';
}

function toApiDtEnd(iso) {
  return iso ? `${iso}T23:59:59` : '';
}

export default function Products() {
  const [view, setView] = useState('packer'); // 'packer' | 'purchase'
  const [date, setDate] = useState(todayIso);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = (d = date, v = view) => {
    setLoading(true);
    setError(null);
    const start = toApiDt(d);
    const end = toApiDtEnd(d);
    const fn = v === 'purchase' ? fetchProductListPurchase : fetchProductList;
    fn(start, end)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = (v) => {
    setView(v);
    setData(null);
    load(date, v);
  };

  // Packer view: Array of { Category, OrderProductView: [{Name, Quantity}] }
  // Purchase view: Array of { Category, OrderProductView: [{Name, Quantity, Child, Invalid}] }
  const groups = Array.isArray(data) ? data : [];
  const totalItems = groups.reduce((sum, g) => sum + (g.OrderProductView ?? []).length, 0);
  const totalQty = groups.reduce((sum, g) =>
    sum + (g.OrderProductView ?? []).reduce((s, p) => s + Number(p.Quantity ?? 0), 0), 0
  );

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Product List</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4, background: 'var(--gray-100)', borderRadius: 6, padding: 3 }}>
            {['packer', 'purchase'].map(v => (
              <button
                key={v}
                onClick={() => handleView(v)}
                style={{
                  padding: '4px 12px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                  background: view === v ? 'white' : 'transparent',
                  color: view === v ? 'var(--navy)' : 'var(--gray-500)',
                  boxShadow: view === v ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                {v === 'packer' ? 'Packer View' : 'Purchase View'}
              </button>
            ))}
          </div>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="form-input"
            style={{ fontSize: 12, width: 150 }}
          />
          <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => load()}>Load</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: '#fff5f5', border: '1px solid #fcc', borderRadius: 6, color: 'var(--red)', fontSize: 12, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading product list...</div>
      )}

      {!loading && data !== null && (
        <>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <div className="card" style={{ padding: '10px 18px', minWidth: 110 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 2 }}>Categories</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)' }}>{groups.length}</div>
            </div>
            <div className="card" style={{ padding: '10px 18px', minWidth: 110 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 2 }}>Line Items</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)' }}>{totalItems}</div>
            </div>
            <div className="card" style={{ padding: '10px 18px', minWidth: 110 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 2 }}>Total Qty</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--orange)' }}>{Number(totalQty).toLocaleString()}</div>
            </div>
          </div>

          {groups.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>No products found for this date.</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
            {groups.map((group, gi) => {
              const items = group.OrderProductView ?? [];
              const groupQty = items.reduce((s, p) => s + Number(p.Quantity ?? 0), 0);
              return (
                <div key={gi} className="card">
                  <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{group.Category || 'Uncategorized'}</span>
                    <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 700 }}>
                      {Number(groupQty).toLocaleString()} units
                    </span>
                  </div>
                  <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                        <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Product</th>
                        <th style={{ textAlign: 'right', padding: '6px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((p, pi) => (
                        <tr key={pi} style={{
                          borderBottom: '1px solid var(--gray-100)',
                          background: pi % 2 === 0 ? '#fff' : 'var(--gray-50)',
                          opacity: p.Invalid ? 0.4 : 1,
                        }}>
                          <td style={{
                            padding: '6px 12px',
                            paddingLeft: p.Child ? 24 : 12,
                            color: p.Child ? 'var(--gray-600)' : 'var(--navy)',
                            fontStyle: p.Child ? 'italic' : 'normal',
                          }}>
                            {p.Child && <span style={{ marginRight: 4, color: 'var(--gray-400)' }}>↳</span>}
                            {p.Name}
                          </td>
                          <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 700, color: 'var(--navy)' }}>
                            {Number(p.Quantity ?? 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
